import React, { useState, useEffect, useContext } from 'react';
import useSeo from '../hooks/useSeo';
import { AuthContext } from '../context/AuthContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export default function CardDetailsPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Card Details');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const res = await fetch(
          `https://app.yaper.co/api/v1/content_pages/${slug}`
        );
        if (!res.ok) throw new Error('Failed to fetch');
        const json = await res.json();
        setCard(json.data);
      } catch (err) {
        console.error('Card fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCard();
  }, [slug]);

  const truncateAtWord = (str = '', maxLen = 160) => {
    if (!str) return '';
    if (str.length <= maxLen) return str;
    const trimmed = str.slice(0, maxLen);
    const lastSpace = Math.max(
      trimmed.lastIndexOf(' '),
      trimmed.lastIndexOf(',')
    );
    return (lastSpace > 0 ? trimmed.slice(0, lastSpace) : trimmed).trim() + '…';
  };

  const rawTitle = `${card?.creditcardname || 'Credit Card'} Review | Card Academy`;
  const seoTitle = truncateAtWord(rawTitle, 60);
  const rawDesc =
    card?.description || 'Compare credit card rewards, fees and benefits.';
  const seoDescription = truncateAtWord(rawDesc, 165);

  useSeo({
    title: seoTitle,
    description: seoDescription,
    canonical: `https://cardacademy.co${location.pathname}`,
    og: {
      title: seoTitle,
      description: seoDescription,
      image: card?.cardImage || 'https://cardacademy.co/social-share.png',
    },
  });

  if (loading)
    return (
      <div style={{ padding: '2rem', fontFamily: 'Titillium Web, sans-serif' }}>
        Loading...
      </div>
    );
  if (!card)
    return (
      <div style={{ padding: '2rem', fontFamily: 'Titillium Web, sans-serif' }}>
        Card not found
      </div>
    );

  (() => {
    let f = {};

    if (Array.isArray(card.features)) {
      f['Product Details'] = card.features;
    } else if (card.features && typeof card.features === 'object') {
      f = { ...card.features };
    }

    if (Array.isArray(card.benefits)) {
      f['Key Features and Benefits'] = (
        f['Key Features and Benefits'] || []
      ).concat(card.benefits);
    }

    if (!Array.isArray(f['Fees & Charges'])) {
      const fees = [];
      if (card.fees) {
        fees.push({ heading: 'Fees', value: card.fees });
      }
      if (card.annualFee) {
        fees.push({ heading: 'Annual Fee', value: card.annualFee });
      }
      if (fees.length) f['Fees & Charges'] = fees;
    }

    card.features = f;
  })();

  const getEmbedUrl = (link) => {
    try {
      const url = new URL(link);
      let id = url.searchParams.get('v');
      if (
        !id &&
        (url.hostname.includes('youtube.com') ||
          url.hostname.includes('youtu.be'))
      ) {
        if (url.pathname.includes('/embed/'))
          id = url.pathname.split('/embed/')[1];
        else if (url.pathname.includes('/watch'))
          id = url.searchParams.get('v');
        else if (url.hostname === 'youtu.be') id = url.pathname.slice(1);
      }
      return id ? `https://www.youtube.com/embed/${id}` : '';
    } catch {
      return '';
    }
  };

  /*
  ==========================
  Apply Button
  ==========================
  */
  const handleApplyClick = () => {
    if (!card.affiliateLink) return;

    if (!user) {
      navigate('/login');
    } else {
      const url = new URL(card.affiliateLink);
      url.searchParams.set('p1', user.id); // user.id = 106225
      window.location.href = url.toString();
    }
  };

  const tabs = [
    'Card Details',
    'Benefits & Features',
    'Rates & Fees',
    'Full Details',
  ];

  const safeSplit = (item) => {
    if (typeof item !== 'string') return [String(item), ''];
    const hasColon = item.includes(':');
    const [label, ...rest] = hasColon ? item.split(':') : [item];
    const value = hasColon ? rest.join(':').trim() : '';
    return [label, value];
  };

  /*
  ==========================
  Tab Content
  ==========================
  */
  const renderTabContent = () => {
    switch (activeTab) {
      case 'Card Details':
        return isMobile ? (
          <div style={mobileStyles.cardOverview}>
            <h3 style={mobileStyles.cardOverviewTitle}>Card Overview</h3>
            <div
              style={mobileStyles.cardOverviewText}
              dangerouslySetInnerHTML={{ __html: card.description || '' }}
            />
          </div>
        ) : (
          <div
            style={styles.contentText}
            dangerouslySetInnerHTML={{ __html: card.description || '' }}
          />
        );

      case 'Benefits & Features':
        return (
          <div style={isMobile ? mobileStyles.benefitsFeatures : {}}>
            <h3 style={isMobile ? mobileStyles.benefitsFeaturesTitle : {}}>
              Card Benefits & Features
            </h3>
            <ul style={styles.list}>
              {(card.features['Key Features and Benefits'] || []).map(
                (item, idx) => (
                  <li key={idx} style={styles.listItem}>
                    {typeof item === 'string' ? item : JSON.stringify(item)}
                  </li>
                )
              )}
            </ul>
          </div>
        );

      case 'Rates & Fees':
        return (
          <div style={{ padding: '1rem' }}>
            <h3
              style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '0.5rem',
              }}
            >
              Rates & Fees
            </h3>
            <p style={{ marginBottom: '1.5rem', color: '#444' }}>
              Detailed breakdown of the rates and fees associated with this
              card.
            </p>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #ccc',
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <tbody>
                {(card.features['Fees & Charges'] || []).map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td
                      style={{
                        padding: '12px 10px',
                        fontWeight: 'bold',
                        backgroundColor: '#f9f9f9',
                        width: '35%',
                        verticalAlign: 'top',
                      }}
                    >
                      {item.heading}
                    </td>
                    <td
                      style={{
                        padding: '12px 10px',
                        verticalAlign: 'top',
                        color: '#333',
                      }}
                    >
                      {item.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'Full Details':
        return (
          <div style={styles.detailsContainer}>
            {card.features['Product Details']?.length > 0 && (
              <div>
                <h3>Product Details</h3>
                <ul style={styles.list}>
                  {card.features['Product Details'].map((item, idx) => (
                    <li key={idx} style={styles.listItem}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {card.features['Pros']?.length > 0 && (
              <div>
                <h3>Pros</h3>
                <ul style={styles.list}>
                  {card.features['Pros'].map((item, idx) => (
                    <li key={idx} style={styles.listItem}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {card.features['Cons']?.length > 0 && (
              <div>
                <h3>Cons</h3>
                <ul style={styles.list}>
                  {card.features['Cons'].map((item, idx) => (
                    <li key={idx} style={styles.listItem}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {card.features['Key Features and Benefits']?.length > 0 && (
              <div>
                <h3>Key Features and Benefits</h3>
                <ul style={styles.list}>
                  {card.features['Key Features and Benefits'].map(
                    (item, idx) => (
                      <li key={idx} style={styles.listItem}>
                        {item}
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}
            {card.features['Eligibility Criteria']?.length > 0 && (
              <div>
                <h3>Eligibility Criteria</h3>
                <ul style={styles.list}>
                  {card.features['Eligibility Criteria'].map((item, idx) => (
                    <li key={idx} style={styles.listItem}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Fees & Charges table */}
            {(card.features['Fees & Charges'] || []).length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h3>Fees and Charges</h3>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px',
                  }}
                >
                  <tbody>
                    {card.features['Fees & Charges'].map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                        <td
                          style={{
                            padding: '10px',
                            fontWeight: 'bold',
                            width: '35%',
                            backgroundColor: '#f9f9f9',
                          }}
                        >
                          {item.heading}
                        </td>
                        <td style={{ padding: '10px', color: '#333' }}>
                          {item.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Full HTML details from API */}
            {card.details && (
              <div dangerouslySetInnerHTML={{ __html: card.details }} />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  /*
  ==========================
  Render Page
  ==========================
  */
  return (
    <div style={isMobile ? mobileStyles.pageWrapper : styles.pageWrapper}>
      <button
        onClick={() => navigate(-1)}
        style={isMobile ? mobileStyles.backButton : styles.backButton}
      >
        ← Back to Cards
      </button>

      <div style={isMobile ? mobileStyles.mainContent : styles.mainContent}>
        {/* Blue Card Section */}
        <div
          style={
            isMobile ? mobileStyles.blueCardSection : styles.blueCardSection
          }
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '192px',
              height: '93px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            }}
          >
            <img
              src={card.cardImage || 'https://placehold.co/176x77?text=Card'}
              alt={card.creditcardname}
              style={{
                width: '176px',
                height: '77px',
                objectFit: 'contain',
                display: 'block',
              }}
              onError={(e) => {
                e.target.src = 'https://placehold.co/176x77?text=Not+Found';
              }}
            />
          </div>

          <div
            style={
              isMobile
                ? mobileStyles.cardNameRatingWrapper
                : { flex: 1, minWidth: '220px' }
            }
          >
            <div
              style={
                isMobile
                  ? mobileStyles.cardNameRatingFlex
                  : {
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      flexWrap: 'wrap',
                    }
              }
            >
              <h2
                style={
                  isMobile
                    ? mobileStyles.cardName
                    : { fontSize: '1.75rem', margin: 0, color: '#fff' }
                }
              >
                {card.creditcardname}
              </h2>
            </div>
            {isMobile && (
              <div style={mobileStyles.ratingMobile}>
                <div style={mobileStyles.ratingValueMobile}>
                  {Number(card.rating || 0).toFixed(1)}
                </div>
                <div style={mobileStyles.ratingLabelMobile}>
                  ★ <span style={{ color: '#fff' }}>out of 5</span>
                </div>
              </div>
            )}
            {/* Bank info if available */}
            {card.bank && (
              <div
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '0.9rem',
                  marginTop: '0.25rem',
                }}
              >
                {card.bank}
              </div>
            )}
          </div>

          {!isMobile && (
            <div style={{ textAlign: 'right', minWidth: '80px' }}>
              <div
                style={{ fontSize: '2rem', fontWeight: 'bold', color: '#fff' }}
              >
                {Number(card.rating || 0).toFixed(1)}
              </div>
              <div style={{ fontSize: '0.95rem', color: '#ffd700' }}>
                ★ <span style={{ color: '#fff' }}>out of 5</span>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        {isMobile ? (
          <div style={mobileStyles.tabsBarWrapper}>
            <div style={mobileStyles.tabsBar}>
              {tabs.map((tab) => (
                <div
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    ...mobileStyles.tabItem,
                    borderBottom:
                      activeTab === tab
                        ? '2px solid #0056b3'
                        : '2px solid transparent',
                    color: activeTab === tab ? '#0056b3' : '#444',
                  }}
                >
                  {tab}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={styles.tabsBar}>
            {tabs.map((tab) => (
              <div
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  ...styles.tabItem,
                  borderBottom:
                    activeTab === tab
                      ? '2px solid #1e3a8a'
                      : '2px solid transparent',
                  color: activeTab === tab ? '#1e3a8a' : '#444',
                }}
              >
                {tab}
              </div>
            ))}
          </div>
        )}

        <div
          style={isMobile ? mobileStyles.tabContentCard : styles.tabContentBox}
        >
          {renderTabContent()}
        </div>
      </div>

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div style={styles.sidebar}>
          <div style={styles.applyBox}>
            <h3 style={styles.applyTitle}>Apply for this card</h3>
            <p style={styles.applyDesc}>
              Ready to enjoy the benefits of the {card.creditcardname}? Apply
              now to get started.
            </p>
            <button style={styles.applyButton} onClick={handleApplyClick}>
              APPLY NOW
            </button>
            <p style={styles.applyNote}>
              By clicking "Apply Now", you will be redirected to the issuer's
              secure application page.
            </p>
          </div>

          {card.bank && (
            <div style={styles.aboutBox}>
              <h4 style={styles.aboutTitle}>About {card.bank.split(' ')[0]}</h4>
              <p style={styles.aboutDesc}>
                {card.bank} offers best-in-class credit cards from premium to
                lifetime free options.
              </p>
              <button
                style={styles.aboutButton}
                onClick={() => {
                  const bankSlug = card.bank.toLowerCase().replace(/\s+/g, '-');
                  navigate(`/partner-banks/${bankSlug}`, {
                    state: { bank: { name: card.bank } },
                  });
                }}
              >
                View all {card.bank.split(' ')[0]} Cards
              </button>
            </div>
          )}

          {getEmbedUrl(card.youtubeLink) && (
            <div style={styles.videoBox}>
              <iframe
                title="Card Video"
                src={getEmbedUrl(card.youtubeLink)}
                style={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  height: 'auto',
                  display: 'block',
                  border: 0,
                }}
                allowFullScreen
              />
            </div>
          )}
        </div>
      )}

      {/* Mobile Sidebar */}
      {isMobile && (
        <>
          {getEmbedUrl(card.youtubeLink) && (
            <div style={mobileStyles.videoCardMobile}>
              <iframe
                width="100%"
                height="240"
                src={getEmbedUrl(card.youtubeLink)}
                title="Card Video"
                frameBorder="0"
                allowFullScreen
              />
            </div>
          )}
          <div style={mobileStyles.applyBoxMobile}>
            <h3 style={mobileStyles.applyTitleMobile}>Apply for this card</h3>
            <p style={mobileStyles.applyDescMobile}>
              Ready to enjoy the benefits of the {card.creditcardname}? Apply
              now to get started.
            </p>
            <button
              style={mobileStyles.applyButtonMobile}
              onClick={handleApplyClick}
            >
              APPLY NOW
            </button>
            <p style={mobileStyles.applyNoteMobile}>
              By clicking "Apply Now", you will be redirected to the issuer's
              secure application page.
            </p>
          </div>

          {card.bank && (
            <div style={mobileStyles.aboutBoxMobile}>
              <h4 style={mobileStyles.aboutTitleMobile}>
                About {card.bank.split(' ')[0]}
              </h4>
              <p style={mobileStyles.aboutDescMobile}>
                {card.bank} offers best-in-class credit cards from premium to
                lifetime free options.
              </p>
              <button
                style={mobileStyles.aboutButtonMobile}
                onClick={() => {
                  const bankSlug = card.bank.toLowerCase().replace(/\s+/g, '-');
                  navigate(`/partner-banks/${bankSlug}`, {
                    state: { bank: { name: card.bank } },
                  });
                }}
              >
                View all {card.bank.split(' ')[0]} Cards
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  pageWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    fontFamily: 'Titillium Web, sans-serif',
    background: '#f0f6ff',
    padding: '2rem',
  },
  backButton: {
    position: 'absolute',
    top: '1.5rem',
    left: '2rem',
    background: 'none',
    border: 'none',
    color: '#1e3a8a',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  mainContent: {
    flex: 2,
    background: '#fff',
    borderRadius: '12px',
    padding: '2rem',
    marginRight: '2rem',
  },
  blueCardSection: {
    width: '100%',
    background: 'linear-gradient(to right, #003366, #0059b3)',
    borderRadius: '12px',
    padding: '1.5rem 2rem',
    marginBottom: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    flexWrap: 'wrap',
    gap: '1.5rem',
  },
  tabsBar: {
    display: 'flex',
    marginTop: '1rem',
    borderBottom: '1px solid #ddd',
  },
  tabItem: {
    marginRight: '1.5rem',
    paddingBottom: '0.5rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  tabContentBox: { padding: '1.5rem 0' },
  contentText: { color: '#444', fontSize: '1rem', lineHeight: 1.6 },
  list: {
    paddingLeft: '1.5rem',
    fontSize: '1rem',
    color: '#444',
    lineHeight: 1.6,
  },
  listItem: { marginBottom: '0.5rem' },
  sidebar: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2rem' },
  applyBox: {
    background: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    textAlign: 'center',
  },
  applyTitle: { margin: 0, fontSize: '1.25rem', fontWeight: 700 },
  applyDesc: { fontSize: '0.9rem', color: '#444', margin: '0.5rem 0' },
  applyButton: {
    background: '#1e3a8a',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '0.75rem 1.5rem',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  applyNote: { fontSize: '0.75rem', color: '#666', marginTop: '1rem' },
  aboutBox: {
    background: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    textAlign: 'center',
  },
  aboutTitle: { margin: 0, fontSize: '1rem', fontWeight: 600 },
  aboutDesc: { fontSize: '0.85rem', color: '#444', margin: '0.5rem 0' },
  aboutButton: {
    background: 'none',
    border: '1px solid #1e3a8a',
    borderRadius: '6px',
    padding: '0.5rem 1rem',
    color: '#1e3a8a',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  videoBox: { overflow: 'hidden', borderRadius: '8px', background: '#fff' },
  detailsContainer: { marginTop: '2rem' },
};

const mobileStyles = {
  pageWrapper: {
    fontFamily: 'Titillium Web, sans-serif',
    background: '#f0f6ff',
    padding: '1rem 0',
  },
  backButton: {
    position: 'relative',
    top: 0,
    left: '1rem',
    background: 'none',
    border: 'none',
    color: '#000',
    cursor: 'pointer',
    fontSize: '1rem',
    marginBottom: '1rem',
  },
  mainContent: {
    flex: 1,
    background: 'transparent',
    borderRadius: '0',
    padding: '0',
    marginRight: '0',
  },
  blueCardSection: {
    width: 'calc(100% - 2rem)',
    background: 'linear-gradient(to right, #003366, #0059b3)',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    flexWrap: 'wrap',
    gap: '1rem',
    margin: '0 auto',
  },
  cardNameRatingWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  cardNameRatingFlex: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
  },
  cardName: {
    fontSize: '1.2rem',
    fontWeight: 600,
    margin: 0,
    textAlign: 'center',
    color: '#fff',
  },
  ratingMobile: { textAlign: 'center', marginTop: '0.5rem' },
  ratingValueMobile: { fontSize: '1.8rem', fontWeight: 'bold', color: '#fff' },
  ratingLabelMobile: { fontSize: '0.8rem', color: '#ffd700' },
  tabsBarWrapper: {
    position: 'relative',
    overflow: 'hidden',
    margin: '0 auto 1rem',
    marginTop: '0.5rem',
    width: 'calc(100% - 2rem)',
    background: '#fff',
    borderRadius: '12px',
  },
  tabsBar: {
    display: 'flex',
    marginTop: '1rem',
    borderBottom: '1px solid #ddd',
  },
  tabItem: {
    flexShrink: 0,
    textAlign: 'center',
    padding: '0.8rem 1rem',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  tabContentCard: {
    width: 'calc(100% - 2rem)',
    margin: '0 auto 1rem',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    padding: '1rem',
  },
  cardOverview: { padding: '0 1rem' },
  cardOverviewTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    color: '#333',
  },
  cardOverviewText: { fontSize: '0.9rem', color: '#555', lineHeight: 1.5 },
  benefitsFeatures: { padding: '0 1rem' },
  benefitsFeaturesTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    color: '#333',
  },
  videoCardMobile: {
    width: 'calc(100% - 2rem)',
    margin: '0 auto 1rem',
    background: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    padding: '0.75rem',
  },
  applyBoxMobile: {
    width: 'calc(100% - 2rem)',
    background: '#fff',
    borderRadius: '12px',
    padding: '1.5rem 1rem',
    textAlign: 'center',
    margin: '0 auto 1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  applyTitleMobile: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#333',
  },
  applyDescMobile: { fontSize: '0.85rem', color: '#555', margin: '0.5rem 0' },
  applyButtonMobile: {
    background: '#1e3a8a',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '0.75rem 1.5rem',
    cursor: 'pointer',
    fontSize: '1rem',
    width: '90%',
    maxWidth: '300px',
  },
  applyNoteMobile: { fontSize: '0.7rem', color: '#666', marginTop: '1rem' },
  aboutBoxMobile: {
    width: 'calc(100% - 2rem)',
    background: '#fff',
    borderRadius: '12px',
    padding: '1.5rem 1rem',
    textAlign: 'center',
    margin: '0 auto 1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  aboutTitleMobile: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#333',
  },
  aboutDescMobile: { fontSize: '0.85rem', color: '#555', margin: '0.5rem 0' },
  aboutButtonMobile: {
    background: 'none',
    border: '1px solid #1e3a8a',
    borderRadius: '6px',
    padding: '0.5rem 1rem',
    color: '#1e3a8a',
    cursor: 'pointer',
    fontSize: '0.9rem',
    width: '90%',
    maxWidth: '300px',
  },
};
